/**
 * SummaryAmortizationTab Component
 * Amortization schedule display within the Contract Summary step
 */

import React, { useState } from 'react';
import { Box, Alert } from '@mui/material';
import AmortizationScheduleTable from '../../../AmortizationScheduleTable';
import ScheduleEntryOverrideDialog from '../../../ScheduleEntryOverrideDialog';
import ScheduleOverridesModal from '../../../ScheduleOverridesModal';
import { usePermissions } from '../../../../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../../../../contexts/PermissionConstants';
import { apiGet, apiPost } from '../../../../../utils/apiClient';
import logger from '../../../../../utils/logger';
import { usePDFGenerator } from '../../../../../hooks/usePDFGenerator';
import { format } from 'date-fns';

function SummaryAmortizationTab({ contract, schedule, onRegenerate }) {
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const { generateAmortizationSchedule, isGenerating } = usePDFGenerator();
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedOverrideEntry, setSelectedOverrideEntry] = useState(null);
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);
  const [overridesModalEntry, setOverridesModalEntry] = useState(null);

  const handleRegenerateSchedule = async () => {
    try {
      try {
        const overridesResp = await apiGet(`/amendments/contract/${contract.id}/overrides`);
        const overrides = overridesResp?.data || overridesResp || [];
        const appliedCount = (Array.isArray(overrides) ? overrides : [])
          .filter(o => o.status === 'approved').length;
        if (appliedCount > 0) {
          // eslint-disable-next-line no-alert
          const proceed = window.confirm(
            `This contract has ${appliedCount} applied schedule override${appliedCount === 1 ? '' : 's'}. ` +
            'Regenerating will discard them. Continue?'
          );
          if (!proceed) return;
        }
      } catch (overrideErr) {
        logger.warn('Could not check schedule overrides before regen', { message: overrideErr.message });
      }

      await apiPost(`/amortization/regenerate/${contract.id}`);
      if (onRegenerate) onRegenerate();
    } catch (err) {
      logger.error('Error regenerating schedule', { message: err.message });
      alert('Failed to regenerate schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const scheduleData = {
        contractReference: contract.reference,
        investorName: contract.investor_name,
        productName: contract.product_type,
        capitalAmount: contract.capital_amount,
        interestRate: contract.interest_rate,
        rateType: contract.rate_type,
        currencyCode: contract.currency_code,
        depositDate: contract.deposit_date,
        maturityDate: contract.maturity_date,
        term: contract.investment_term_value,
        termUnit: contract.investment_term,
        payments: schedule.map((payment) => ({
          dueDate: payment.due_date || payment.payment_date,
          principal: payment.principal_amount || payment.principal || 0,
          interest: payment.interest_amount || payment.interest || 0,
          totalPayment: payment.total_payment || (parseFloat(payment.principal_amount || payment.principal || 0) + parseFloat(payment.interest_amount || payment.interest || 0)),
          balance: payment.remaining_balance || payment.balance || 0,
        })),
      };

      const filename = `Amortization_Schedule_${contract.reference || contract.id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await generateAmortizationSchedule(scheduleData, filename);
    } catch (err) {
      logger.error('Error downloading amortization PDF', { message: err.message });
      alert('Failed to generate amortization schedule PDF. Please try again.');
    }
  };

  if (!schedule || schedule.length === 0) {
    return (
      <Alert severity="info">
        No amortization schedule has been generated for this contract yet.
      </Alert>
    );
  }

  return (
    <Box>
      <AmortizationScheduleTable
        schedule={schedule}
        showAllRows
        contractId={contract.id}
        contract={contract}
        onRegenerate={handleRegenerateSchedule}
        hideRegenerateButton
        canAdmin={canAdmin}
        onDownloadPDF={handleDownloadPDF}
        isGeneratingPDF={isGenerating}
        onOverrideRequest={(entry) => {
          setSelectedOverrideEntry(entry);
          setOverrideDialogOpen(true);
        }}
        onShowOverrides={(entry) => {
          setOverridesModalEntry(entry);
          setOverridesModalOpen(true);
        }}
      />
      <ScheduleEntryOverrideDialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        contract={contract}
        scheduleEntry={selectedOverrideEntry}
        onSubmitSuccess={() => {
          setOverrideDialogOpen(false);
          if (onRegenerate) onRegenerate();
        }}
      />
      <ScheduleOverridesModal
        open={overridesModalOpen}
        onClose={() => {
          setOverridesModalOpen(false);
          setOverridesModalEntry(null);
        }}
        contract={contract}
        scheduleEntry={overridesModalEntry}
        onChanged={() => { if (onRegenerate) onRegenerate(); }}
      />
    </Box>
  );
}

export default SummaryAmortizationTab;
